import * as React from 'react';
import { Autocomplete, Button, Dialog, DialogActions, DialogContent, DialogTitle, TextField } from '@mui/material';
import { styled, lighten, darken } from '@mui/system';
import { useTranslation } from "react-i18next";
import { observer } from 'mobx-react-lite';
import { Assignment, Publisher } from '../../../domain/Publisher';
import { useStores } from '../../../stores';

interface Props {
    publisher?: Publisher;
    isOpen: boolean;
    onClose: () => void;
}

const GroupHeader = styled('div')(({ theme }) => ({
    position: 'sticky',
    top: '-8px',
    padding: '4px 10px',
    color: theme.palette.primary.main,
    backgroundColor:
        theme.palette.mode === 'light'
            ? lighten(theme.palette.primary.light, 0.85)
            : darken(theme.palette.primary.main, 0.8),
}));

const GroupItems = styled('ul')({
    padding: 0,
});

const SchedulePublisherAssignmentsDialog = observer((props: Props) => {
    const { appointmentStore, publisherStore } = useStores();
    const { t } = useTranslation();
    const [assignments, setAssignments] = React.useState<Assignment[]>([]);

    React.useEffect(() => {
        if (props.isOpen && appointmentStore.assignments.length === 0) {
            appointmentStore.getAll();
        }
    }, [appointmentStore, props.isOpen]);

    React.useEffect(() => {
        if (props.isOpen && props.publisher) {
            setAssignments(props.publisher.assignments || []);
        }
    }, [props.isOpen, props.publisher]);

    const handleClose = () => {
        props.onClose();
    };

    const handleSave = () => {
        if (!props.publisher) {
            return;
        }
        const payload: Publisher = {
            ...props.publisher,
            assignments,
        };
        publisherStore.updatePublisher(props.publisher.id, payload, () => {
            publisherStore.getAllPublishers();
            props.onClose();
        });
    };

    return (
        <Dialog open={props.isOpen} onClose={handleClose} fullWidth maxWidth="md" PaperProps={{ sx: { minHeight: 420 } }}>
            <DialogTitle>{t("assignments")}</DialogTitle>
            <DialogContent sx={{ overflow: "visible" }}>
                <Autocomplete
                    disablePortal
                    multiple
                    id="publisher-assignments"
                    options={appointmentStore.assignments}
                    value={assignments}
                    groupBy={(assignment: Assignment) => assignment.category}
                    getOptionLabel={(assignment: Assignment) => assignment && t(assignment.name)}
                    isOptionEqualToValue={(option, value) => option.id === value.id}
                    renderInput={(params) => <TextField {...params} margin="normal" label={t("assignments")} />}
                    renderGroup={(params) => (
                        <li key={params.group}>
                            <GroupHeader>{t(params.group)}</GroupHeader>
                            <GroupItems>{params.children}</GroupItems>
                        </li>
                    )}
                    onChange={(_, assignment) => {
                        setAssignments(assignment);
                    }}
                />
            </DialogContent>
            <DialogActions>
                <Button onClick={handleClose}>{t("cancel")}</Button>
                <Button onClick={handleSave} disabled={!props.publisher}>
                    {t("submit")}
                </Button>
            </DialogActions>
        </Dialog>
    );
});

export default SchedulePublisherAssignmentsDialog;

